import React from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download } from "lucide-react";
import { formatRestrictedSqlValue } from "./customReportMeasures.js";
import {
  buildColumnHeaderGroups,
  getHierarchicalRowSpan,
  PERIOD_COMPARISON_AXIS_FIELDS,
  PERIOD_COMPARISON_PERIODS,
  PERIOD_COMPARISON_TYPES,
  tupleKey,
} from "./customReportVisualizationConfig.js";
import { escapePivotCsvValue } from "./MultidimensionalPivotTable.jsx";

const displayDimension = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const isMissingSortValue = (value) =>
  value === null || value === undefined || value === "" || value === "-";

const comparePeriodValues = (left, right, direction) => {
  const leftMissing = isMissingSortValue(left);
  const rightMissing = isMissingSortValue(right);
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  const comparison =
    Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
      ? leftNumber - rightNumber
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: "base",
        });
  return direction === "desc" ? -comparison : comparison;
};

const normalizeDescriptor = (descriptor = {}) => ({
  key: String(descriptor?.key || ""),
  label: String(descriptor?.label || descriptor?.key || ""),
  dataType: String(descriptor?.dataType ?? descriptor?.data_type ?? ""),
  parameterKey: String(descriptor?.parameter_key ?? descriptor?.parameterKey ?? ""),
});

const normalizeMetric = (metric = {}) => ({
  id: String(metric?.id || ""),
  key: String(metric?.key || ""),
  label: String(metric?.label || metric?.id || metric?.key || ""),
  aggregation: String(metric?.aggregation || ""),
  polarity: String(metric?.polarity || ""),
  format: String(metric?.format || "number"),
  drilldownMode: String(metric?.drilldownMode ?? metric?.drilldown_mode ?? ""),
});

const formatPeriodAxisLabel = (axisField) =>
  PERIOD_COMPARISON_AXIS_FIELDS.find((option) => option.value === axisField)?.label ||
  String(axisField || "CRM Lead Created At")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const getPeriodComparisonTableModel = (visualization = {}) => {
  const data = visualization?.data || {};
  const rawConfig = data.periodComparison ?? data.period_comparison;
  const responseConfig =
    rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig) ? rawConfig : {};
  const comparisonType = String(
    responseConfig.comparisonType ?? responseConfig.comparison_type ?? ""
  );
  const dimensions = (Array.isArray(responseConfig.dimensions)
    ? responseConfig.dimensions
    : []
  )
    .map(normalizeDescriptor)
    .filter((dimension) => dimension.key);
  const periods = (Array.isArray(responseConfig.periods) ? responseConfig.periods : [])
    .map((period) => ({
      id: String(period?.id || ""),
      label: String(period?.label || period?.id || ""),
      metrics: (Array.isArray(period?.metrics) ? period.metrics : [])
        .map(normalizeMetric)
        .filter((metric) => metric.key),
    }))
    .filter((period) => period.id && period.metrics.length > 0);
  const dimensionByKey = new Map(
    dimensions.map((dimension) => [String(dimension.key), dimension])
  );
  const employeeDimension = dimensionByKey.get("employee_id");
  const areaDimension = dimensionByKey.get("area_label");
  const isDetail = ["employee_positive_areas", "employee_negative_areas"].includes(
    comparisonType
  );
  const visibleDimensions = [
    ...(employeeDimension ? [employeeDimension] : []),
    ...(isDetail && areaDimension
      ? [
          {
            ...areaDimension,
            label:
              comparisonType === "employee_positive_areas" ? "Positive Area" : "Negative Area",
          },
        ]
      : []),
  ];
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const truncated = Boolean(responseConfig.truncated);
  const rowLimit = Number(responseConfig.rowLimit ?? responseConfig.row_limit);
  const isValid = Boolean(
    PERIOD_COMPARISON_TYPES.some((option) => option.value === comparisonType) &&
      employeeDimension &&
      (!isDetail || areaDimension) &&
      periods.length > 0 &&
      periods.every((period) => period.metrics.length > 0)
  );

  return {
    comparisonType,
    visibleDimensions,
    periods,
    rows,
    truncated,
    rowLimit: Number.isFinite(rowLimit) && rowLimit > 0 ? rowLimit : null,
    isValid,
  };
};

const getMetricLabel = (comparisonType, metric) => {
  if (comparisonType === "team_areas") {
    const labels = {
      positive_average: "Positive Avg Score",
      positive_count: "Positive Customers",
      negative_average: "Negative Avg Score",
      negative_count: "Negative Customers",
    };
    return labels[metric.id] || metric.label;
  }
  const labels = { average: "Average Score", count: "Customer Count" };
  return labels[metric.id] || metric.label;
};

const getMetricFormat = (metric) => {
  if (metric.aggregation === "count") return "integer";
  if (metric.aggregation === "average") return "decimal";
  return metric.format;
};

export const getGenericPeriodComparisonTableModel = (visualization = {}) => {
  const data = visualization?.data || {};
  const rawConfig = data.periodComparison ?? data.period_comparison;
  const responseConfig =
    rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig) ? rawConfig : {};
  const dimensions = (Array.isArray(responseConfig.dimensions)
    ? responseConfig.dimensions
    : []
  )
    .map(normalizeDescriptor)
    .filter((dimension) => dimension.key);
  const hasExplicitAxes =
    Array.isArray(responseConfig.rowDimensions) ||
    Array.isArray(responseConfig.columnDimensions);
  const rowDimensions = (
    Array.isArray(responseConfig.rowDimensions)
      ? responseConfig.rowDimensions
      : dimensions
  )
    .map(normalizeDescriptor)
    .filter((dimension) => dimension.key);
  const columnDimensions = (
    Array.isArray(responseConfig.columnDimensions)
      ? responseConfig.columnDimensions
      : []
  )
    .map(normalizeDescriptor)
    .filter((dimension) => dimension.key);
  const metrics = (Array.isArray(responseConfig.metrics) ? responseConfig.metrics : [])
    .map((metric) => ({
      id: String(metric?.id || ""),
      label: String(metric?.label || metric?.id || ""),
      format: String(metric?.format || "number"),
      drilldownMode: String(metric?.drilldownMode ?? metric?.drilldown_mode ?? ""),
    }))
    .filter((metric) => metric.id);
  const periods = (Array.isArray(responseConfig.periods) ? responseConfig.periods : [])
    .map((period) => ({
      id: String(period?.id || ""),
      label: String(period?.label || period?.id || ""),
      valueKey: String(period?.valueKey ?? period?.value_key ?? ""),
    }))
    .filter((period) => period.id && period.valueKey);
  const periodIds = periods.map((period) => period.id);
  const orderedPeriodIds = PERIOD_COMPARISON_PERIODS.filter((periodId) =>
    periodIds.includes(periodId)
  );
  const periodsAreValid =
    periodIds.length > 0 &&
    periodIds.length <= PERIOD_COMPARISON_PERIODS.length &&
    new Set(periodIds).size === periodIds.length &&
    periodIds.every((periodId, index) => periodId === orderedPeriodIds[index]);
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const measurePlacement =
    (responseConfig.measurePlacement ?? responseConfig.measure_placement) === "columns"
      ? "columns"
      : "rows";
  const rowLimit = Number(responseConfig.rowLimit ?? responseConfig.row_limit);
  const dimensionKeys = dimensions.map((dimension) => dimension.key);
  const axisKeys = [...rowDimensions, ...columnDimensions].map(
    (dimension) => dimension.key
  );
  const axesAreValid =
    new Set(axisKeys).size === axisKeys.length &&
    (!hasExplicitAxes ||
      (axisKeys.length === dimensionKeys.length &&
        axisKeys.every((key) => dimensionKeys.includes(key))));
  const isValid = Boolean(
    responseConfig.mode === "generic" &&
      metrics.length > 0 &&
      periodsAreValid &&
      axesAreValid
  );
  return {
    axisField: String(responseConfig.axisField ?? responseConfig.axis_field ?? ""),
    timezone: String(responseConfig.timezone || "Asia/Kolkata"),
    dimensions,
    rowDimensions,
    columnDimensions,
    metrics,
    measurePlacement,
    periods,
    rows,
    truncated: Boolean(responseConfig.truncated),
    rowLimit: Number.isFinite(rowLimit) && rowLimit > 0 ? rowLimit : null,
    isValid,
  };
};

export const buildGenericPeriodComparisonPivotModel = (model) => {
  const rowKeys = model.rowDimensions.map((dimension) => dimension.key);
  const columnKeys = model.columnDimensions.map((dimension) => dimension.key);
  const rowTuples = [];
  const columnTuples = [];
  const seenRows = new Set();
  const seenColumns = new Set();
  const valueByCellMetric = new Map();
  (Array.isArray(model.rows) ? model.rows : []).forEach((row) => {
    const rowValues = rowKeys.map((key) => row?.[key]);
    const columnValues = columnKeys.map((key) => row?.[key]);
    const rowIdentity = tupleKey(rowValues);
    const columnIdentity = tupleKey(columnValues);
    if (!seenRows.has(rowIdentity)) {
      seenRows.add(rowIdentity);
      rowTuples.push({ key: rowIdentity, values: rowValues });
    }
    if (!seenColumns.has(columnIdentity)) {
      seenColumns.add(columnIdentity);
      columnTuples.push({ key: columnIdentity, values: columnValues });
    }
    valueByCellMetric.set(
      `${rowIdentity}\u0000${columnIdentity}\u0000${String(row?.metric_id || "")}`,
      row
    );
  });
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  const compareTuples = (left, right) => {
    const length = Math.max(left.values.length, right.values.length);
    for (let index = 0; index < length; index += 1) {
      const comparison = collator.compare(
        left.values[index] === null || left.values[index] === undefined
          ? ""
          : String(left.values[index]),
        right.values[index] === null || right.values[index] === undefined
          ? ""
          : String(right.values[index])
      );
      if (comparison !== 0) return comparison;
    }
    return 0;
  };
  rowTuples.sort(compareTuples);
  columnTuples.sort(compareTuples);
  return { rowTuples, columnTuples, valueByCellMetric };
};

const GenericPeriodComparisonTable = ({ visualization, reportLevel, onCellClick }) => {
  const [sortConfig, setSortConfig] = React.useState(null);
  const model = getGenericPeriodComparisonTableModel(visualization);
  if (!model.isValid) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        The period comparison response does not match its configured dimensions, metrics, and periods.
      </div>
    );
  }
  const metricById = new Map(model.metrics.map((metric) => [metric.id, metric]));
  const metrics = model.metrics.map((metric) => {
    const matchingRow = model.rows.find(
      (row) => String(row?.metric_id || "") === metric.id
    );
    return {
      ...metric,
      label: String(matchingRow?.metric_label || metric.label || metric.id),
      format: String(matchingRow?.metric_format || metric.format || "number"),
    };
  });
  const pivotModel = buildGenericPeriodComparisonPivotModel(model);
  const columnTuples = pivotModel.columnTuples.length
    ? pivotModel.columnTuples
    : [{ key: tupleKey([]), values: [] }];
  const hasMultipleMetrics = metrics.length > 1;
  const measuresInRows = model.measurePlacement === "rows";
  const hasRowDimensions = model.rowDimensions.length > 0;
  const rowHeaderCount = measuresInRows
    ? model.rowDimensions.length + 1
    : hasRowDimensions
      ? model.rowDimensions.length
      : 1;
  const headerDepth =
    1 +
    model.columnDimensions.length +
    (!measuresInRows && hasMultipleMetrics ? 1 : 0);
  const metricColumnMultiplier = measuresInRows ? 1 : metrics.length;
  const valueColumnCount =
    model.periods.length * columnTuples.length * metricColumnMultiplier;
  const getValueRow = (rowTuple, columnTuple, metric) =>
    pivotModel.valueByCellMetric.get(
      `${rowTuple.key}\u0000${columnTuple.key}\u0000${metric.id}`
    );
  const sortedRowTuples = [...pivotModel.rowTuples];
  if (!measuresInRows && sortConfig?.kind === "column-value") {
    const period = model.periods.find((item) => item.id === sortConfig.periodId);
    const columnTuple = columnTuples.find(
      (item) => item.key === sortConfig.columnTupleKey
    );
    const metric = metrics.find((item) => item.id === sortConfig.metricId);
    if (period && columnTuple && metric) {
      sortedRowTuples.sort((left, right) =>
        comparePeriodValues(
          getValueRow(left, columnTuple, metric)?.[period.valueKey],
          getValueRow(right, columnTuple, metric)?.[period.valueKey],
          sortConfig.direction
        )
      );
    }
  }
  const measureRows = pivotModel.rowTuples.flatMap((rowTuple, rowIndex) =>
    metrics.map((metric, metricIndex) => ({
      rowTuple,
      rowIndex,
      metric,
      metricIndex,
    }))
  );
  if (measuresInRows && sortConfig?.kind === "row-metric") {
    measureRows.sort((left, right) =>
      comparePeriodValues(
        left.metric.label,
        right.metric.label,
        sortConfig.direction
      )
    );
  }
  if (measuresInRows && sortConfig?.kind === "row-value") {
    const period = model.periods.find((item) => item.id === sortConfig.periodId);
    const columnTuple = columnTuples.find(
      (item) => item.key === sortConfig.columnTupleKey
    );
    if (period && columnTuple) {
      measureRows.sort((left, right) =>
        comparePeriodValues(
          getValueRow(left.rowTuple, columnTuple, left.metric)?.[period.valueKey],
          getValueRow(right.rowTuple, columnTuple, right.metric)?.[period.valueKey],
          sortConfig.direction
        )
      );
    }
  }
  const cycleSort = (nextSort) => {
    setSortConfig((current) => {
      const isSameColumn =
        current?.kind === nextSort.kind &&
        current?.periodId === nextSort.periodId &&
        current?.columnTupleKey === nextSort.columnTupleKey &&
        current?.metricId === nextSort.metricId;
      if (!isSameColumn) {
        return { ...nextSort, direction: "asc" };
      }
      if (current.direction === "asc") {
        return { ...nextSort, direction: "desc" };
      }
      return null;
    });
  };
  const renderSortIcon = (isActive) => {
    if (!isActive) {
      return <ChevronsUpDown className="h-3 w-3 text-gray-300 group-hover:text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-3 w-3 text-indigo-600" />
    ) : (
      <ArrowDown className="h-3 w-3 text-indigo-600" />
    );
  };
  const downloadTable = () => {
    if (measuresInRows) {
      const headers = [
        ...model.rowDimensions.map((dimension) => dimension.label),
        "Metric",
        ...model.periods.flatMap((period) =>
          columnTuples.map((columnTuple) =>
            [period.label, ...columnTuple.values.map(displayDimension)].join(" / ")
          )
        ),
      ];
      const csvRows = measureRows.map(({ rowTuple, metric }) => [
        ...rowTuple.values,
        metric.label,
        ...model.periods.flatMap((period) =>
          columnTuples.map(
            (columnTuple) => getValueRow(rowTuple, columnTuple, metric)?.[period.valueKey]
          )
        ),
      ]);
      const csv = [headers, ...csvRows]
        .map((row) => row.map(escapePivotCsvValue).join(","))
        .join("\n");
      const link = document.createElement("a");
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
      link.download = `${visualization.title || "period_comparison"}_data.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    const headers = [
      ...(hasRowDimensions
        ? model.rowDimensions.map((dimension) => dimension.label)
        : ["Scope"]),
      ...model.periods.flatMap((period) =>
        columnTuples.flatMap((columnTuple) =>
          metrics.map((metric) =>
            [
              period.label,
              ...columnTuple.values.map(displayDimension),
              ...(hasMultipleMetrics ? [metric.label] : []),
            ].join(" / ")
          )
        )
      ),
    ];
    const csvRows = sortedRowTuples.map((rowTuple) => [
      ...(hasRowDimensions ? rowTuple.values : ["All Customers"]),
      ...model.periods.flatMap((period) =>
        columnTuples.flatMap((columnTuple) =>
          metrics.map(
            (metric) => getValueRow(rowTuple, columnTuple, metric)?.[period.valueKey]
          )
        )
      ),
    ]);
    const csv = [headers, ...csvRows]
      .map((row) => row.map(escapePivotCsvValue).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    link.download = `${visualization.title || "period_comparison"}_data.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const renderValueCell = (rowTuple, columnTuple, metric, period, key) => {
    const valueRow = getValueRow(rowTuple, columnTuple, metric);
    const descriptor = metricById.get(metric.id) || metric;
    const rawValue = valueRow?.[period.valueKey];
    const isDrillable =
      descriptor?.drilldownMode === "exact_count" &&
      (reportLevel === "calls" || Boolean(visualization?.drilldown?.context)) &&
      Number(rawValue) > 0;
    return (
      <td
        key={key}
        onClick={
          isDrillable
            ? () =>
                onCellClick?.({
                  measureId: metric.id,
                  metricLabel: metric.label,
                  periodId: period.id,
                  dimensions: [
                    ...model.rowDimensions.map((dimension, index) => ({
                      key: dimension.key,
                      parameterKey: dimension.parameterKey,
                      label: dimension.label,
                      value: rowTuple.values[index],
                    })),
                    ...model.columnDimensions.map((dimension, index) => ({
                      key: dimension.key,
                      parameterKey: dimension.parameterKey,
                      label: dimension.label,
                      value: columnTuple.values[index],
                    })),
                  ],
                })
            : undefined
        }
        className={`border-b border-l border-gray-100 px-4 py-3 text-right text-sm tabular-nums ${
          isDrillable
            ? "cursor-pointer text-indigo-600 underline hover:text-indigo-700"
            : "text-gray-800"
        }`}
      >
        {formatRestrictedSqlValue(
          rawValue,
          valueRow?.metric_format || descriptor.format
        )}
      </td>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 px-6 pb-3 pt-4">
        <div>
          <div className="text-xl font-semibold">
            {visualization.title || `Visualization ${visualization.id}`}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Period basis: {formatPeriodAxisLabel(model.axisField)} · {model.timezone}
          </div>
          <div className="mt-0.5 text-[11px] text-gray-500">
            Rows: {model.rowDimensions.map((dimension) => dimension.label).join(" / ") || (measuresInRows ? "Measures" : "All Customers")}
            {measuresInRows && model.rowDimensions.length ? " / Measures" : ""}
            {model.columnDimensions.length
              ? ` · Columns: ${model.columnDimensions.map((dimension) => dimension.label).join(" / ")}`
              : ""}
            {!hasMultipleMetrics ? ` · Measure: ${metrics[0]?.label || "Value"}` : ""}
          </div>
        </div>
        <button
          type="button"
          aria-label={`Download ${visualization.title || "period comparison"}`}
          onClick={downloadTable}
          className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
        >
          <Download size={20} />
        </button>
      </div>
      {model.truncated ? (
        <div className="mx-6 mb-3 shrink-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Showing only the first
          {model.rowLimit ? ` ${model.rowLimit.toLocaleString()}` : ""} rows. Narrow the report filters for a complete result.
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
        <table
          className="w-full min-w-max border-separate border-spacing-0"
          data-testid="generic-period-comparison-table"
        >
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50">
              {(hasRowDimensions
                ? model.rowDimensions
                : measuresInRows
                  ? []
                  : [{ key: "__scope__", label: "Scope" }]
              ).map((dimension) => (
                <th
                  key={`generic-period-dimension-${dimension.key}`}
                  rowSpan={headerDepth}
                  className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {dimension.label}
                </th>
              ))}
              {measuresInRows ? (
                <th
                  rowSpan={headerDepth}
                  aria-sort={
                    sortConfig?.kind === "row-metric"
                      ? sortConfig.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  <button
                    type="button"
                    className="group flex items-center gap-1"
                    onClick={() => cycleSort({ kind: "row-metric" })}
                  >
                    <span>Metric</span>
                    {renderSortIcon(sortConfig?.kind === "row-metric")}
                  </button>
                </th>
              ) : null}
              {model.periods.map((period) => {
                const canSortPeriod =
                  model.columnDimensions.length === 0 &&
                  (measuresInRows || !hasMultipleMetrics);
                const periodSort = measuresInRows
                  ? {
                      kind: "row-value",
                      periodId: period.id,
                      columnTupleKey: columnTuples[0]?.key,
                    }
                  : {
                      kind: "column-value",
                      periodId: period.id,
                      columnTupleKey: columnTuples[0]?.key,
                      metricId: metrics[0]?.id,
                    };
                const isActive =
                  canSortPeriod &&
                  sortConfig?.kind === periodSort.kind &&
                  sortConfig?.periodId === periodSort.periodId &&
                  sortConfig?.columnTupleKey === periodSort.columnTupleKey &&
                  sortConfig?.metricId === periodSort.metricId;
                return (
                  <th
                    key={`generic-period-${period.id}`}
                    colSpan={columnTuples.length * metricColumnMultiplier}
                    aria-sort={
                      canSortPeriod
                        ? isActive
                          ? sortConfig.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                        : undefined
                    }
                    className="border-b border-l border-gray-200 bg-gray-50 px-4 py-2 text-center text-xs font-semibold text-gray-700"
                  >
                    {canSortPeriod ? (
                      <button
                        type="button"
                        className="group mx-auto flex items-center gap-1 whitespace-nowrap"
                        onClick={() => cycleSort(periodSort)}
                      >
                        <span>{period.label}</span>
                        {renderSortIcon(isActive)}
                      </button>
                    ) : (
                      period.label
                    )}
                  </th>
                );
              })}
            </tr>
            {model.columnDimensions.map((dimension, level) => (
              <tr
                key={`generic-period-column-level-${dimension.key}`}
                className="bg-gray-50"
              >
                {model.periods.flatMap((period) =>
                  buildColumnHeaderGroups(
                    columnTuples,
                    level,
                    metricColumnMultiplier
                  ).map(
                    (group) => {
                      const columnTuple =
                        level === model.columnDimensions.length - 1
                          ? columnTuples.find(
                              (item) => `${item.key}:${level}` === group.key
                            )
                          : null;
                      const valueSort = columnTuple
                        ? measuresInRows
                          ? {
                              kind: "row-value",
                              periodId: period.id,
                              columnTupleKey: columnTuple.key,
                            }
                          : !hasMultipleMetrics
                            ? {
                                kind: "column-value",
                                periodId: period.id,
                                columnTupleKey: columnTuple.key,
                                metricId: metrics[0]?.id,
                              }
                            : null
                        : null;
                      const isActive = Boolean(
                        valueSort &&
                          sortConfig?.kind === valueSort.kind &&
                          sortConfig?.periodId === valueSort.periodId &&
                          sortConfig?.columnTupleKey === valueSort.columnTupleKey &&
                          sortConfig?.metricId === valueSort.metricId
                      );
                      return (
                      <th
                        key={`generic-period-${period.id}-${group.key}`}
                        colSpan={group.colSpan}
                        aria-sort={
                          valueSort
                            ? isActive
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                            : undefined
                        }
                        className="border-b border-l border-gray-200 bg-gray-50 px-4 py-2 text-center text-xs font-semibold text-gray-600"
                      >
                        {valueSort ? (
                          <button
                            type="button"
                            className="group mx-auto flex items-center gap-1 whitespace-nowrap"
                            onClick={() => cycleSort(valueSort)}
                          >
                            <span>{displayDimension(group.value)}</span>
                            {renderSortIcon(isActive)}
                          </button>
                        ) : (
                          displayDimension(group.value)
                        )}
                      </th>
                      );
                    }
                  )
                )}
              </tr>
            ))}
            {!measuresInRows && hasMultipleMetrics ? (
              <tr className="bg-gray-50">
                {model.periods.flatMap((period) =>
                  columnTuples.flatMap((columnTuple) =>
                    metrics.map((metric) => {
                      const isActive =
                        sortConfig?.kind === "column-value" &&
                        sortConfig?.periodId === period.id &&
                        sortConfig?.columnTupleKey === columnTuple.key &&
                        sortConfig?.metricId === metric.id;
                      return (
                      <th
                        key={`generic-period-${period.id}-${columnTuple.key}-${metric.id}`}
                        aria-sort={
                          isActive
                            ? sortConfig.direction === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                        className="border-b border-l border-gray-200 bg-gray-50 px-4 py-2 text-right text-[11px] font-medium text-gray-500"
                      >
                        <button
                          type="button"
                          className="group ml-auto flex items-center justify-end gap-1 whitespace-nowrap"
                          onClick={() =>
                            cycleSort({
                              kind: "column-value",
                              periodId: period.id,
                              columnTupleKey: columnTuple.key,
                              metricId: metric.id,
                            })
                          }
                        >
                          <span>{metric.label}</span>
                          {renderSortIcon(isActive)}
                        </button>
                      </th>
                      );
                    })
                  )
                )}
              </tr>
            ) : null}
          </thead>
          <tbody>
            {sortedRowTuples.length === 0 ? (
              <tr>
                <td
                  colSpan={rowHeaderCount + valueColumnCount}
                  className="px-4 py-3 text-sm text-gray-500"
                >
                  No data available
                </td>
              </tr>
            ) : measuresInRows ? (
              measureRows.map(({ rowTuple, rowIndex, metric, metricIndex }) => {
                const hasActiveMeasureRowSort = Boolean(
                  sortConfig && ["row-metric", "row-value"].includes(sortConfig.kind)
                );
                return (
                  <tr
                    key={`generic-period-row-${rowTuple.key}-${metric.id}`}
                    className="hover:bg-gray-50"
                  >
                    {hasRowDimensions && (hasActiveMeasureRowSort || metricIndex === 0)
                      ? rowTuple.values.map((value, level) => {
                          const rowSpan = hasActiveMeasureRowSort
                            ? 1
                            : getHierarchicalRowSpan(
                                sortedRowTuples,
                                rowIndex,
                                level
                              ) * metrics.length;
                          return rowSpan > 0 ? (
                            <th
                              key={`generic-period-row-${rowTuple.key}-${level}`}
                              rowSpan={rowSpan}
                              className="border-b border-gray-100 bg-white px-4 py-3 text-left align-top text-sm font-medium text-gray-900"
                            >
                              {displayDimension(value)}
                            </th>
                          ) : null;
                        })
                      : null}
                    <th className="border-b border-gray-100 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900">
                      {metric.label}
                    </th>
                    {model.periods.flatMap((period) =>
                      columnTuples.map((columnTuple) =>
                        renderValueCell(
                          rowTuple,
                          columnTuple,
                          metric,
                          period,
                          `generic-period-row-${rowTuple.key}-${metric.id}-${period.id}-${columnTuple.key}`
                        )
                      )
                    )}
                  </tr>
                );
              })
            ) : (
              sortedRowTuples.map((rowTuple, rowIndex) => (
                <tr key={`generic-period-row-${rowTuple.key}`} className="hover:bg-gray-50">
                  {hasRowDimensions ? (
                    rowTuple.values.map((value, level) => {
                      const rowSpan = getHierarchicalRowSpan(
                        sortedRowTuples,
                        rowIndex,
                        level
                      );
                      return rowSpan > 0 ? (
                        <th
                          key={`generic-period-row-${rowTuple.key}-${level}`}
                          rowSpan={rowSpan}
                          className="border-b border-gray-100 bg-white px-4 py-3 text-left align-top text-sm font-medium text-gray-900"
                        >
                          {displayDimension(value)}
                        </th>
                      ) : null;
                    })
                  ) : (
                    <th className="border-b border-gray-100 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900">
                      All Customers
                    </th>
                  )}
                  {model.periods.flatMap((period) =>
                    columnTuples.flatMap((columnTuple) =>
                      metrics.map((metric) =>
                        renderValueCell(
                          rowTuple,
                          columnTuple,
                          metric,
                          period,
                          `generic-period-row-${rowTuple.key}-${period.id}-${columnTuple.key}-${metric.id}`
                        )
                      )
                    )
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PeriodComparisonTable = ({ visualization, reportLevel, onCellClick }) => {
  const responseConfig =
    visualization?.data?.periodComparison ?? visualization?.data?.period_comparison;
  if (responseConfig?.mode === "generic") {
    return (
      <GenericPeriodComparisonTable
        visualization={visualization}
        reportLevel={reportLevel}
        onCellClick={onCellClick}
      />
    );
  }
  const model = getPeriodComparisonTableModel(visualization);

  if (!model.isValid) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        The period comparison response does not match its configured dimensions and metrics.
      </div>
    );
  }

  const downloadTable = () => {
    const headers = [
      ...model.visibleDimensions.map((dimension) => dimension.label),
      ...model.periods.flatMap((period) =>
        period.metrics.map(
          (metric) => `${period.label} / ${getMetricLabel(model.comparisonType, metric)}`
        )
      ),
    ];
    const csvRows = model.rows.map((row) => [
      ...model.visibleDimensions.map((dimension) => row?.[dimension.key]),
      ...model.periods.flatMap((period) =>
        period.metrics.map((metric) => row?.[metric.key])
      ),
    ]);
    const csv = [headers, ...csvRows]
      .map((row) => row.map(escapePivotCsvValue).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    link.download = `${visualization.title || "period_comparison"}_data.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 px-6 pb-3 pt-4">
        <div>
          <div className="text-xl font-semibold">
            {visualization.title || `Visualization ${visualization.id}`}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Period basis: CRM Lead Created At · Asia/Kolkata
          </div>
        </div>
        <button
          type="button"
          aria-label={`Download ${visualization.title || "period comparison"}`}
          onClick={downloadTable}
          className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
        >
          <Download size={20} />
        </button>
      </div>
      {model.truncated ? (
        <div className="mx-6 mb-3 shrink-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Showing only the first
          {model.rowLimit ? ` ${model.rowLimit.toLocaleString()}` : ""} rows. Narrow the report filters for a complete result.
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
        <table
          className="w-full min-w-max border-separate border-spacing-0"
          data-testid="period-comparison-table"
        >
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50">
              {model.visibleDimensions.map((dimension) => (
                <th
                  key={`period-dimension-${dimension.key}`}
                  rowSpan={2}
                  className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {dimension.label}
                </th>
              ))}
              {model.periods.map((period) => (
                <th
                  key={`period-group-${period.id}`}
                  colSpan={period.metrics.length}
                  className="border-b border-l border-gray-200 bg-gray-50 px-4 py-2 text-center text-xs font-semibold text-gray-700"
                >
                  {period.label}
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50">
              {model.periods.flatMap((period) =>
                period.metrics.map((metric) => (
                  <th
                    key={`period-metric-${period.id}-${metric.key}`}
                    className="border-b border-l border-gray-200 bg-gray-50 px-4 py-2 text-right text-[11px] font-medium text-gray-500"
                  >
                    {getMetricLabel(model.comparisonType, metric)}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {model.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    model.visibleDimensions.length +
                    model.periods.reduce((count, period) => count + period.metrics.length, 0)
                  }
                  className="px-4 py-3 text-sm text-gray-500"
                >
                  No data available
                </td>
              </tr>
            ) : (
              model.rows.map((row, rowIndex) => (
                <tr key={`period-row-${rowIndex}`} className="hover:bg-gray-50">
                  {model.visibleDimensions.map((dimension) => (
                    <th
                      key={`period-row-${rowIndex}-${dimension.key}`}
                      className="border-b border-gray-100 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900"
                    >
                      {displayDimension(row?.[dimension.key])}
                    </th>
                  ))}
                  {model.periods.flatMap((period) =>
                    period.metrics.map((metric) => (
                      <td
                        key={`period-row-${rowIndex}-${period.id}-${metric.key}`}
                        className="border-b border-l border-gray-100 px-4 py-3 text-right text-sm tabular-nums text-gray-800"
                      >
                        {formatRestrictedSqlValue(row?.[metric.key], getMetricFormat(metric))}
                      </td>
                    ))
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PeriodComparisonTable;
